"use client";

import { useEffect, useMemo, useState } from 'react';
import { getAdminSocket } from '@/services/socket';
import authService from '@/services/authService';
import { useConnectRequest } from '../contexts/ConnectRequestContext';
import { LiveChatRoom } from '@/services/liveChatService';
import { dispatchAdminNotification } from './notifications/NotificationsProvider';

export default function ConnectRequestListener() {
  const { setConnectRequest } = useConnectRequest();
  const isAuthed = authService.isAuthenticated();
  const [socketReady, setSocketReady] = useState(false);

  const socket = useMemo(() => {
    try {
      return isAuthed ? getAdminSocket() : null;
    } catch (e) {
      console.error('Error getting admin socket:', e);
      return null;
    }
  }, [isAuthed]);

  // Đảm bảo socket đã kết nối trước khi đăng ký event listener
  useEffect(() => {
    if (!socket) {
      setSocketReady(false);
      return;
    }

    // Nếu socket đã kết nối, đánh dấu ready ngay
    if (socket.connected) {
      setSocketReady(true);
      return;
    }

    // Nếu socket chưa kết nối, đợi event connect
    const handleConnect = () => {
      setSocketReady(true);
    };

    const handleDisconnect = () => {
      setSocketReady(false);
    };

    socket.on('connect', handleConnect);
    socket.on('disconnect', handleDisconnect);

    // Đảm bảo socket kết nối nếu chưa kết nối
    if (!socket.connected && !socket.active) {
      try {
        socket.connect();
      } catch (e) {
        // Ignore connection errors
      }
    }

    return () => {
      socket.off('connect', handleConnect);
      socket.off('disconnect', handleDisconnect);
    };
  }, [socket]);

  // Đăng ký event listener chỉ khi socket đã sẵn sàng
  useEffect(() => {
    if (!socket || !socketReady) return;

    const onUserRequestAdmin = (payload: { roomId: string; user: LiveChatRoom['user']; requestedAt: Date }) => {
      // Hiển thị modal yêu cầu kết nối ở bất kỳ trang nào
      setConnectRequest({
        roomId: payload.roomId,
        user: payload.user,
        requestedAt: payload.requestedAt
      });

      // Gửi thông báo vào hệ thống thông báo của admin
      dispatchAdminNotification({
        title: `Khách yêu cầu kết nối`,
        message: `${payload.user?.name || payload.roomId || 'Khách'} đang chờ phản hồi`,
        category: 'messages',
        href: '/admin/chat'
      });
    };

    // Thông báo khi có tin nhắn mới từ người dùng (khi đang ở trang khác)
    const onNewMessage = (payload: { roomId: string; message: { content: string; timestamp: string | number }; user: LiveChatRoom['user'] }) => {
      dispatchAdminNotification({
        title: `Tin nhắn mới`,
        message: `${payload.user?.name || payload.roomId || 'Khách'}: ${payload.message?.content || ''}`.slice(0, 140),
        category: 'messages',
        href: '/admin/chat'
      });
    };

    socket.on('user_request_admin', onUserRequestAdmin);
    socket.on('new_message', onNewMessage);

    return () => {
      socket.off('user_request_admin', onUserRequestAdmin);
      socket.off('new_message', onNewMessage);
    };
  }, [socket, socketReady, setConnectRequest]);

  return null; // Component này không render gì
}

